import { useState, useEffect, useRef } from "react";
import { logger } from "@/lib/logger";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Activity, Mail, MailCheck, Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { z } from "zod";
import { analytics } from "@/lib/analytics";
import { ROUTES } from '@/lib/routes';


const emailSchema = z.string().email("Invalid email address");
const passwordSchema = z.string().min(6, "Password must be at least 6 characters");

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { translate } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  // Check for pending invite context - use state so it reacts to changes
  const [isInviteFlow, setIsInviteFlow] = useState(false);
  const [pendingInviteEmail, setPendingInviteEmail] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("signin");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync invite context from sessionStorage on mount and on navigate
  useEffect(() => {
    const token = sessionStorage.getItem("pendingInviteToken");
    const inviteEmail = sessionStorage.getItem("pendingInviteEmail");
    if (token) {
      setIsInviteFlow(true);
      setPendingInviteEmail(inviteEmail);
      setActiveTab("signup");
      if (inviteEmail) {
        setEmail(inviteEmail);
      }
    }
  }, []);

  // When showing the verify-email screen, poll for session in case the user
  // clicks the email link in another tab / window.
  useEffect(() => {
    if (!showVerifyEmail) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      return;
    }

    // Also listen for auth state changes (fires instantly if same tab)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'USER_UPDATED') && session) {
        setEmailVerified(true);
        setTimeout(() => handleSuccessfulAuth(session.user.id), 1500);
      }
    });

    // Poll every 3 s as a fallback for cross-tab verification
    pollingRef.current = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        clearInterval(pollingRef.current!);
        setEmailVerified(true);
        setTimeout(() => handleSuccessfulAuth(session.user.id), 1500);
      }
    }, 3000);

    return () => {
      subscription.unsubscribe();
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVerifyEmail]);

  const handleSuccessfulAuth = async (userId: string) => {
    try {
      // If there's a pending invite token, send the user back to the invite page
      const pendingToken = sessionStorage.getItem("pendingInviteToken");
      if (pendingToken) {
        sessionStorage.removeItem("pendingInviteToken");
        sessionStorage.removeItem("pendingInviteEmail");
        navigate(ROUTES.INVITE(pendingToken));
        return;
      }

      // Check if user has profile (indicates account is set up)
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", userId)
        .single();

      if (!profile) {
        // New user without profile - go to onboarding
        navigate(ROUTES.ONBOARDING_START);
        return;
      }

      // Check if user is a player - route them to player portal
      // Uses organization-centric roles (user_organization_roles) not legacy user_roles
      const { data: orgRoles } = await supabase
        .from("user_organization_roles")
        .select("role")
        .eq("user_id", userId);

      // If user only has player_viewer role(s), redirect to player portal
      const isOnlyPlayer = orgRoles && 
        orgRoles.length > 0 && 
        orgRoles.every(r => r.role === "player_viewer");
        
      if (isOnlyPlayer) {
        navigate(ROUTES.PLAYER_CHECK_IN);
        return;
      }

      // Staff user - go to dashboard
      navigate(ROUTES.DASHBOARD);
    } catch (error) {
      logger.error("Error checking auth status:", error);
      // On error, try onboarding
      navigate(ROUTES.ONBOARDING_START);
    }
  };

  useEffect(() => {
    let mounted = true;

    // Check if user is already logged in
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session && mounted) {
        handleSuccessfulAuth(session.user.id);
      }
    };
    
    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session && mounted) {
        // Use setTimeout to avoid blocking the auth callback
        setTimeout(() => {
          if (mounted) {
            handleSuccessfulAuth(session.user.id);
          }
        }, 100);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate inputs
      emailSchema.parse(email);
      passwordSchema.parse(password);

      if (!name.trim()) {
        toast({
          title: translate("auth_name_required"),
          description: translate("auth_name_required_desc"),
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      const redirectUrl = `${window.location.origin}/auth`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: name,
          },
        },
      });

      if (error) {
        if (error.message.includes("already registered")) {
          toast({
            title: translate("auth_account_exists"),
            description: translate("auth_account_exists_desc"),
            variant: "destructive",
          });
        } else {
          toast({
            title: translate("auth_signup_failed"),
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      // Show the "check your email" confirmation page
      analytics.track('auth.signup_completed', { method: 'email' });
      analytics.track('auth.terms_accepted', { version: '2025-01' });
      setShowVerifyEmail(true);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: translate("auth_validation_error"),
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate inputs
      emailSchema.parse(email);
      passwordSchema.parse(password);

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast({
          title: translate("auth_signin_failed"),
          description: error.message,
          variant: "destructive",
        });
      } else {
        analytics.track('auth.login', { method: 'email' });
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: translate("auth_validation_error"),
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast({
        title: "Enter your email",
        description: "Please enter your email address above, then click 'Forgot password?'",
        variant: "destructive",
      });
      return;
    }

    try {
      emailSchema.parse(email);
    } catch {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setIsLoading(false);

    if (error) {
      toast({
        title: "Reset failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Check your inbox",
        description: `A password reset link has been sent to ${email}.`,
      });
    }
  };

  if (showVerifyEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <Card className="shadow-xl">
            <CardContent className="pt-8 pb-8 text-center space-y-6">
              <div className="flex justify-center">
                <div className={`p-4 rounded-full transition-colors duration-500 ${emailVerified ? "bg-primary/20" : "bg-primary/10"}`}>
                  {emailVerified ? (
                    <CheckCircle2 className="h-10 w-10 text-primary" />
                  ) : (
                    <MailCheck className="h-10 w-10 text-primary" />
                  )}
                </div>
              </div>

              {emailVerified ? (
                <>
                  <h2 className="text-2xl font-bold text-primary">Email verified!</h2>
                  <p className="text-muted-foreground">Taking you to AlphaSignal…</p>
                  <div className="flex justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold">{translate("auth_verify_email_title")}</h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {translate("auth_verify_email_body", { email })}
                  </p>

                  {/* Animated "waiting" indicator */}
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Waiting for verification…</span>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    {translate("auth_verify_email_no_email")}{" "}
                    <button
                      onClick={() => setShowVerifyEmail(false)}
                      className="text-primary hover:underline font-medium"
                    >
                      {translate("auth_verify_email_try_again")}
                    </button>
                    .
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Activity className="h-10 w-10 text-primary" />
            <span className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              AlphaSignal
            </span>
          </div>
          <p className="text-muted-foreground">{translate("auth_platform_tagline")}</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle>{translate("auth_welcome")}</CardTitle>
            <CardDescription>{translate("auth_welcome_desc")}</CardDescription>
          </CardHeader>
          <CardContent>
            {isInviteFlow && (
              <div className="mb-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-primary">{translate("auth_invite_banner_title")}</p>
                    <p className="text-muted-foreground">
                      {translate("auth_invite_banner_desc", { email: pendingInviteEmail ?? "" })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">{translate("auth_tab_signin")}</TabsTrigger>
                <TabsTrigger value="signup">{translate("auth_tab_signup")}</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">{translate("auth_label_email")}</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder={translate("auth_placeholder_email")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="signin-password">{translate("auth_label_password")}</Label>
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="signin-password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? translate("auth_signing_in") : translate("auth_btn_signin")}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">{translate("auth_label_fullname")}</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder={translate("auth_placeholder_fullname")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">{translate("auth_label_email")}</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder={translate("auth_placeholder_email")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">{translate("auth_label_password")}</Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {translate("auth_password_hint")}
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? translate("auth_creating_account") : translate("auth_btn_create_account")}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground pt-1">
                    {translate("auth_signup_terms_note")}{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {translate("auth_signup_terms_link")}
                    </a>{" "}
                    {translate("auth_signup_terms_and")}{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {translate("auth_signup_privacy_link")}
                    </a>
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
